import React, { useState, useCallback } from 'react';
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Box
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { bffApiClient } from '@api/bff/client';

type Props = {
  imageId: string;
  scriptId: string;
  runId: string;
};

const MeasurementImageButton: React.FC<Props> = ({
  imageId,
  scriptId,
  runId
}) => {
  const [open, setOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleClick = useCallback(async () => {
    setOpen(true);
    setLoading(true);
    try {
      const response = await bffApiClient.get(
        `/api/mqtt-scripts/${scriptId}/runs/${runId}/images/${imageId}`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(response.data);
      setImageSrc(url);
    } catch {
      setImageSrc(null);
    } finally {
      setLoading(false);
    }
  }, [imageId, scriptId, runId]);

  const handleClose = useCallback(() => {
    setOpen(false);
    if (imageSrc) {
      URL.revokeObjectURL(imageSrc);
      setImageSrc(null);
    }
  }, [imageSrc]);

  return (
    <>
      <IconButton size="small" onClick={handleClick}>
        <CameraAltIcon fontSize="small" />
      </IconButton>
      <Dialog open={open} onClose={handleClose} maxWidth="lg">
        <DialogTitle>Measurement Image</DialogTitle>
        <DialogContent>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {!loading && imageSrc && (
            <Box
              component="img"
              src={imageSrc}
              alt="Measurement capture"
              sx={{ maxWidth: '100%', display: 'block' }}
            />
          )}
          {!loading && !imageSrc && (
            <Box sx={{ p: 2, textAlign: 'center' }}>Failed to load image</Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MeasurementImageButton;
